import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { orderBy } from 'lodash'
import { PackageIcon, CalendarIcon, UsersIcon, CalendarDays } from 'lucide-react'

import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import { Checkbox } from '../../ui/checkbox'
import { Switch } from '../../ui/switch'
import Avatar from '../Avatar/Avatar'
import MultiSelectStudent from '../KlassForm/MultiSelectStudent/MultiSelectStudent'
import CurrencyInput from '../CurrencyInput/CurrencyInput'

function KlassPackageForm({
  onSubmit,
  price,
  klasses = [],
  title,
  description,
  open = true,
  proratedPricing = true,
  students = [],
  facilityId
}) {
  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      price,
      klasses: klasses.filter((klass) => klass.selected).map((klass) => klass.id),
      title,
      description,
      open,
      proratedPricing,
      students: orderBy(students, ['selected', 'fullName'], ['desc'])
    }
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} aria-label="Class package form" className="min-h-screen bg-gray-100 pb-10">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">New Class Package</h1>
          <p className="mt-2 text-sm text-gray-600">
            Set up your package details, select classes, and manage student enrollments
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <PackageIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Menu Info</h2>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="Tuesdays - 5:00 PM"
                    data-cy="title-input"
                    {...register('title', { required: 'A package must have a title' })}
                    className="mt-2"
                  />
                  <p className="mt-1 text-xs text-gray-500">This is displayed in large bold print on the menu</p>
                  {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>}
                </div>

                <div>
                  <Label htmlFor="price">Price *</Label>
                  <Controller
                    name="price"
                    control={control}
                    rules={{ required: 'A package must have a price set' }}
                    render={({ field, fieldState: { error } }) => (
                      <div className="mt-2">
                        <CurrencyInput
                          id="price"
                          placeholder="$120.00"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          errorMessage={error?.message}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This is the base price of the package. On the menu, this price is pro-rated based on how many
                          classes are left.
                        </p>
                      </div>
                    )}
                  />
                </div>

                <div className="lg:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        id="description"
                        maxLength={200}
                        placeholder="Classes run between Jan 1 and March 15, starting at 8 PM and run for 30 minutes."
                        rows={3}
                        data-cy="description-textarea"
                        className="mt-2"
                      />
                    )}
                  />
                  <p className="mt-1 text-xs text-gray-500">This is displayed below the title on the menu</p>
                  {watch('description') && (
                    <p className="mt-1 text-xs text-gray-500 text-right">
                      {200 - (watch('description')?.length || 0)} characters remaining
                    </p>
                  )}
                </div>

                <div className="lg:col-span-2">
                  <div className="flex items-center space-x-2">
                    <Controller
                      name="open"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="open"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-cy="open-checkbox-input"
                        />
                      )}
                    />
                    <Label htmlFor="open" className="cursor-pointer">
                      Active on menu?
                    </Label>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="space-y-0.5">
                      <Label htmlFor="proratedPricing" className="text-sm font-medium">
                        Enable Prorated Pricing
                      </Label>
                      <p className="text-xs text-gray-500">
                        When enabled, the price will be automatically adjusted based on how many classes remain in the
                        package.
                      </p>
                    </div>
                    <Controller
                      name="proratedPricing"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="proratedPricing"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-cy="prorated-pricing-switch"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Classes</h2>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 space-y-1 text-sm text-gray-600">
                <p>When a student buys this package, we auto-place them in these classes.</p>
                <p>Leevo will only add a student to one class per day for their level.</p>
                <p>If no class supports their level, they will not be able to see this package during registration.</p>
              </div>

              <Controller
                name="klasses"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Label className="mb-3 block text-sm font-medium text-gray-700">Select Classes</Label>
                    <div
                      className={`h-96 overflow-y-auto rounded-lg border-2 bg-white shadow-sm ${
                        error ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      {klasses.filter((klass) => klass.remainingKlassDays > 0).length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                          <p className="text-gray-500 mb-2">You don&apos;t have any upcoming classes.</p>
                          <p data-cy="create-class-link" className="text-sm">
                            <Link
                              className="font-medium text-purple-600 hover:text-purple-700 underline"
                              to={`/facilities/${facilityId}/classes/new`}
                            >
                              Create some classes
                            </Link>{' '}
                            <span className="text-gray-500">
                              with dates in the future to see them appear in this list.
                            </span>
                          </p>
                        </div>
                      )}
                      <ul className="divide-y divide-gray-100">
                        {klasses
                          .filter((klass) => klass.remainingKlassDays > 0)
                          .map((klass) => {
                            const isSelected = new Set(field.value).has(klass.id)
                            return (
                              <li
                                key={klass.id}
                                className={`flex items-start gap-4 px-4 py-4 transition-colors cursor-pointer ${
                                  isSelected ? 'bg-purple-50 hover:bg-purple-100' : 'hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  const valueAsSet = new Set(field.value)
                                  if (valueAsSet.has(klass.id)) {
                                    valueAsSet.delete(klass.id)
                                  } else {
                                    valueAsSet.add(klass.id)
                                  }
                                  field.onChange(Array.from(valueAsSet))
                                }}
                              >
                                <div className="flex-shrink-0 mt-0.5">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => {}}
                                    className="h-5 w-5"
                                    data-cy={`klass-${klass.id}-check-button`}
                                    aria-pressed={isSelected}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-4 mb-2">
                                    <span className="text-base font-semibold text-gray-900 inline-flex items-center gap-1.5">
                                      {klass.dayOfWeek} - {klass.timeOfDay}
                                      {klass.multiDay && (
                                        <CalendarDays className="h-3.5 w-3.5 text-purple-500" />
                                      )}
                                    </span>
                                    <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                                      {klass.startDate} to {klass.endDate}
                                    </span>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex flex-wrap items-center gap-3">
                                      {klass.coaches.map((coach) => (
                                        <div key={coach.id} className="flex items-center gap-2">
                                          <Avatar
                                            fullName={coach.fullName}
                                            cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                                            size={10}
                                            userId={coach.id}
                                          />
                                          <span className="text-base font-medium text-gray-700">
                                            {coach.preferredName || coach.email}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    {klass.supportedSkillLevels.length > 0 && (
                                      <div className="flex items-center gap-1 text-xs text-gray-600">
                                        <span className="font-medium">Levels:</span>
                                        <span className="truncate">
                                          {klass.supportedSkillLevels.map((skillLevel) => skillLevel.name).join(', ')}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            )
                          })}
                      </ul>
                    </div>
                    {error && <p className="mt-2 text-sm text-red-500">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <UsersIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Students</h2>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 space-y-1 text-sm text-gray-600">
                <p>Students are automatically added to a package when registering.</p>
                <p>Add a student to enroll them in all selected classes.</p>
                <p>Remove a student to un-enroll them from all selected classes.</p>
              </div>

              <Controller
                name="students"
                control={control}
                render={({ field }) => {
                  const fieldProps = { ...field }
                  delete fieldProps.ref
                  return <MultiSelectStudent {...fieldProps} onChange={field.onChange} />
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" disabled={isSubmitting} variant="default" size="lg">
            {isSubmitting ? 'Saving...' : 'Save Package'}
          </Button>
        </div>
      </div>
    </form>
  )
}

KlassPackageForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  price: PropTypes.number,
  klasses: PropTypes.array,
  title: PropTypes.string,
  students: PropTypes.array,
  description: PropTypes.string,
  open: PropTypes.bool,
  proratedPricing: PropTypes.bool,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default KlassPackageForm
